import * as Comlink from 'comlink'
import {
  type $Map,
  type AnyDomainEntity,
  type ApplicationTypes,
  Handing,
  type Point,
  type RootEntity,
} from 'metalworks-domain'
import { match } from 'ts-pattern'

import { type Domain, type DomainState } from '@salsita-ai/platform-project-sdk/domain'

import { type OcWorker } from '../oc-worker'
import OCWorker from '../oc-worker?worker'
import { type OcWorkerGenerateRequest, ocWorkerProxyOps } from './oc-shared'

const worker = Comlink.wrap(new OCWorker()) as OcWorker

// TODO: we may need to move this to lambda or generate it on client and send it as base64 to server as part of Finalize data
export const exportStepFile = async (
  state: DomainState<AnyDomainEntity>,
  domain: Domain<
    AnyDomainEntity,
    RootEntity,
    $Map,
    ApplicationTypes.PublishedData,
    ApplicationTypes.SessionData,
    ApplicationTypes.FrontendData
  >
) => {
  const $plateEntity = domain.getRoot$Entity(state)
  if (!$plateEntity) {
    throw new Error('Plate entity not found!')
  }

  const { thickness } = $plateEntity
  const { handing } = $plateEntity
  const $holeEntities = $plateEntity.$holes

  const plateCommand = ocWorkerProxyOps.drawPlate($plateEntity.$segmentInfo)

  const plateSize = {
    x: $plateEntity.$sizeX,
    y: $plateEntity.$sizeY,
  }

  const holeCommands = $holeEntities.map(entity => {
    const coords: Point = {
      x: entity.$absCoords.x,
      y: entity.$absCoords.y - $plateEntity.$sizeY,
    }

    return match(entity)
      .with(
        { type: 'BoltHole' },
        { type: 'DeadboltHole' },
        { type: 'Hole54' },
        { type: 'PlainCylinderHole' },
        { type: 'PlainLocksetHole' },
        { type: 'RimCylinderHole' },
        e => ocWorkerProxyOps.drawCircularHole(e.radius, thickness, coords)
      )
      .with({ type: 'CornerBoltHoles' }, e =>
        ocWorkerProxyOps.drawBoltHoles(e.radius, thickness, coords, plateSize, { horizontal: 4 })
      )
      .with({ type: 'CornerCountersinkHoles' }, e =>
        ocWorkerProxyOps.drawCountersinkHoles(e, thickness, coords, plateSize, { horizontal: 4 }, handing)
      )
      .with({ type: 'KickPlateBoltHoles' }, e =>
        ocWorkerProxyOps.drawBoltHoles(e.radius, thickness, coords, plateSize, {
          horizontal: e.$horizontalCount,
          vertical: e.$verticalCount,
        })
      )
      .with({ type: 'KickPlateCountersinkHoles' }, e =>
        ocWorkerProxyOps.drawCountersinkHoles(
          e,
          thickness,
          coords,
          plateSize,
          {
            horizontal: e.$horizontalCount,
            vertical: e.$verticalCount,
          },
          handing
        )
      )
      .with({ type: 'CamlockHole' }, e => ocWorkerProxyOps.drawCamlockHole(e, thickness, coords))
      .with({ type: 'CountersinkHole' }, e => ocWorkerProxyOps.drawCountersinkHole(e, thickness, coords, handing))
      .with({ type: 'EuroCylinderHoleAu' }, { type: 'EuroCylinderHoleEu' }, e =>
        ocWorkerProxyOps.drawEuroCylinderHole(e, thickness, coords)
      )
      .with({ type: 'OvalCylinderHole' }, e => ocWorkerProxyOps.drawOvalCylinderHole(e, thickness, coords))
      .with({ type: 'PN6Hole' }, e => ocWorkerProxyOps.drawPN6Hole(e, thickness, coords))
      .with({ type: 'PD7Hole' }, e => ocWorkerProxyOps.drawPD7Hole(e, thickness, coords))
      .with({ type: 'RectangularHole' }, { type: 'SquareHole' }, e =>
        ocWorkerProxyOps.drawShapeHole(e, thickness, coords)
      )
      .exhaustive()
  })

  const allCommands = {
    plate: plateCommand,
    holes: holeCommands,
    mirror: handing === Handing.reverse,
  } satisfies OcWorkerGenerateRequest

  const generatedResult = await worker.generate(allCommands)

  return generatedResult
}
